/**
 * documentValidator.js
 *
 * Content-based validation for the 3 deal document types.
 * Each validator inspects extracted text/rows for distinctive keywords
 * and returns { isValid, message } so the UI can reject wrong documents.
 *
 * Patterns derived from reference documents:
 *   OM       → Solana OM.pdf (Colliers)
 *   RentRoll → Rent Roll 4-9-26 (OneSite Rents / Clearworth Residential)
 *   T12      → Solana January 2026 Financials.xlsx (Twelve Month Trailing Income Statement)
 */

// ─── OM (PDF) ─────────────────────────────────────────────────────────────────
// A valid OM will have at least one STRONG keyword, OR multiple FINANCIAL signals.

const OM_STRONG = [
  'offering memorandum',
  'investment offering',
  'confidential offering',
  'confidential information memorandum',
  'marketing package',
  'for sale offering',
  'exclusive offering',
];

const OM_FINANCIAL = [
  'net operating income',
  'noi',
  'cap rate',
  'capitalization rate',
  'gross potential rent',
  'effective gross revenue',
  'effective gross income',
  'total operating expenses',
  'asking price',
  'offering price',
  'sale price',
  'purchase price',
  'cash-on-cash',
  'irr',
  'debt service',
  'dscr',
  'going-in cap',
];

const OM_PROPERTY = [
  'units', 'multifamily', 'apartment', 'year built',
  'occupancy', 'vacancy', 'rent roll',
  'colliers', 'cbre', 'marcus', 'millichap', 'jll', 'cushman',
  'berkadia', 'newmark', 'kw commercial', 'avison',
  'broker', 'brokerage',
];

/**
 * Validate that a PDF is an Offering Memorandum.
 * @param {string} text - Full extracted text from the PDF
 * @returns {{ isValid: boolean, message: string|null }}
 */
export function validateOmDocument(text) {
  if (!text || text.trim().length < 50) {
    // Scanned / image-only PDF — no text layer. Allow it through so the
    // backend vision extraction path (Claude vision) can process it.
    return { isValid: true, message: null };
  }

  const lower = text.toLowerCase();

  const strongMatches   = OM_STRONG.filter(kw => lower.includes(kw)).length;
  const financialMatches = OM_FINANCIAL.filter(kw => lower.includes(kw)).length;
  const propertyMatches  = OM_PROPERTY.filter(kw => lower.includes(kw)).length;

  // Pass if: has a strong OM keyword, OR has ≥2 financial signals AND ≥2 property signals
  const isValid = strongMatches >= 1 || (financialMatches >= 2 && propertyMatches >= 2);

  return {
    isValid,
    message: isValid
      ? null
      : 'This document does not appear to be an Offering Memorandum (OM). ' +
        'A valid OM should contain property details, asking price, NOI, and cap rate information. ' +
        'Please upload the correct OM PDF for this deal.',
  };
}


// ─── Rent Roll (XLS / XLSX) ───────────────────────────────────────────────────
// Reference: OneSite Rents — columns: Floor Plan, Occupied, Vacant, Market Rent,
//            Leased Rent, Occupancy Percent, NTV-NL, NTV-L, Net Leased Percent

const RENT_ROLL_STRONG = [
  'floor plan',
  'avg market rent',
  'avg leased rent',
  'avg effective',
  'occupancy percent',
  'net leased percent',
  'unit status',
  'onesite rents',
  'floor plan group',
  'ntv-nl',
  'ntv-l',
  'rent roll',
  'rentroll',
  'yardi',
  'realpage',
  'appfolio',
  'entrata',
  'resman',
];

const RENT_ROLL_GENERAL = [
  'market rent',
  'leased rent',
  'effective rent',
  'occupied',
  'vacant',
  'occupancy',
  'unit',
  'floor plan',
  'leased',
  'tenant',
  'resident',
  'lease start',
  'lease end',
  'move in',
  'move out',
  'sq ft',
  'bed',
  'bath',
];

/**
 * Validate that an Excel file is a Rent Roll.
 * @param {string[][]} rows - All rows from all sheets (array of arrays)
 * @param {string[]} [sheetNames] - Sheet names
 * @returns {{ isValid: boolean, message: string|null }}
 */
export function validateRentRollDocument(rows, sheetNames = [], extractedText = '') {
  const isPdf = rows.length === 0;

  if (isPdf && (!extractedText || extractedText.trim().length < 20)) {
    // Scanned PDF — allow through for backend vision extraction
    return { isValid: true, message: null };
  }

  const allText = isPdf
    ? extractedText.toLowerCase()
    : rows.flat().map(c => String(c ?? '').toLowerCase().trim()).join(' ');

  const strongMatches  = RENT_ROLL_STRONG.filter(kw => allText.includes(kw)).length;
  const generalMatches = RENT_ROLL_GENERAL.filter(kw => allText.includes(kw)).length;

  const hasUnitConcept = /\bunit\b|\bfloor.?plan\b|\bapt\b|\bsuite\b/.test(allText);
  const hasRentConcept = /\brent\b|\bcharge\b|\brate\b/.test(allText);

  const isValid =
    strongMatches >= 1 ||
    (generalMatches >= 3 && hasUnitConcept && hasRentConcept);

  return {
    isValid,
    message: isValid
      ? null
      : 'This document does not appear to be a Rent Roll. ' +
        'A valid Rent Roll should contain unit/floor plan data, market rent, ' +
        'and occupancy/vacancy information. ' +
        'Please upload the correct Rent Roll PDF or spreadsheet.',
  };
}


// ─── T12 / P&L (XLSX) ────────────────────────────────────────────────────────
// Reference: Twelve Month Trailing Income Statement
//   Sheet name: "Twelve Month Trailing Income St"
//   Row labels: Gross Potential Rent, Gain/Loss to Lease, Vacancy Loss,
//               Total Rental Income, Total Operating Expenses, Total Operating Income
//   GL codes: 4000-xxxx (income), 5100-xxxx (expenses), 6200-xxxx (taxes)

const T12_STRONG = [
  'gross potential rent',
  'total rental income',
  'total operating income',
  'total operating expenses',
  'vacancy loss',
  'gain / loss to lease',
  'gain/loss to lease',
  'twelve month trailing',
  'net operating income',
  'trailing 12',
  't12',
  't-12',
  'trailing twelve',
  'operating statement',
  'profit & loss',
  'p&l',
  'profit and loss',
];

const T12_GENERAL = [
  'gross potential',
  'potential rent',
  'rental income',
  'operating expenses',
  'operating income',
  'vacancy',
  'concessions',
  'bad debt',
  'management fee',
  'insurance',
  'real estate tax',
  'maintenance',
  'payroll',
  'make ready',
  'income statement',
  'trailing',
  'total income',
  'total expenses',
];

// GL code pattern: 4000-0000, 5100-xxxx, etc.
const GL_CODE_PATTERN = /\b[456]\d{3}[-–]\d{4}\b/;

/**
 * Validate that an Excel file is a T12 / P&L financial statement.
 * @param {string[][]} rows - All rows from all sheets
 * @param {string[]} [sheetNames] - Sheet names
 * @returns {{ isValid: boolean, message: string|null }}
 */
export function validateT12Document(rows, sheetNames = [], extractedText = '') {
  const isPdf = rows.length === 0;

  if (isPdf && (!extractedText || extractedText.trim().length < 20)) {
    // Scanned PDF — allow through for backend vision extraction
    return { isValid: true, message: null };
  }

  const allText = isPdf
    ? extractedText.toLowerCase()
    : rows.flat().map(c => String(c ?? '').toLowerCase().trim()).join(' ');

  const sheetText = sheetNames.join(' ').toLowerCase();

  // Check sheet name for T12-like names (only applicable if Excel)
  const hasT12Sheet = !isPdf && /twelve|trailing|t.?12|income.?stat|financial|p.?&?.?l|profit.?loss/.test(sheetText);

  // Check GL account code pattern (very distinctive for property management T12s)
  const hasGLCodes = GL_CODE_PATTERN.test(
    isPdf ? extractedText : rows.flat().map(c => String(c ?? '')).join(' ')
  );

  const strongMatches  = T12_STRONG.filter(kw => allText.includes(kw)).length;
  const generalMatches = T12_GENERAL.filter(kw => allText.includes(kw)).length;

  // Check for monthly date columns (≥3 date-like values in one row = likely T12 column headers)
  const hasMonthColumns = !isPdf && rows.some(row => {
    const dateCount = row.filter(c => {
      const s = String(c ?? '');
      return /\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{2}-\d{2}|(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(s);
    }).length;
    return dateCount >= 3;
  });

  const isValid =
    hasT12Sheet ||
    hasGLCodes ||
    strongMatches >= 1 ||
    (generalMatches >= 4 && (isPdf || hasMonthColumns));

  return {
    isValid,
    message: isValid
      ? null
      : 'This document does not appear to be a T12/P&L financial statement. ' +
        'A valid T12 should contain 12 months of income and expense data with line items like ' +
        '"Gross Potential Rent", "Total Operating Expenses", and "Net Operating Income". ' +
        'Please upload the correct T12/P&L document.',
  };
}


// ─── Master validator ─────────────────────────────────────────────────────────

/**
 * Validate a document against the expected type.
 *
 * @param {'om'|'rentRoll'|'t12'} docType
 * @param {string} extractedText - For PDFs: the full extracted text
 * @param {string[][]} rows      - For Excel: all rows from all sheets
 * @param {string[]} sheetNames  - For Excel: sheet names
 * @returns {{ isValid: boolean, message: string|null }}
 */
export function validateDocument(docType, extractedText = '', rows = [], sheetNames = []) {
  switch (docType) {
    case 'om':
      // OM must be a PDF — reject Excel/spreadsheet files
      if (rows.length > 0) {
        return {
          isValid: false,
          message:
            'The OM slot only accepts PDF files. ' +
            'You appear to have uploaded a spreadsheet. ' +
            'Please upload the Offering Memorandum as a PDF.',
        };
      }
      return validateOmDocument(extractedText);

    case 'rentRoll':
      return validateRentRollDocument(rows, sheetNames, extractedText);

    case 't12':
      return validateT12Document(rows, sheetNames, extractedText);

    default:
      return { isValid: true, message: null }; // unknown type → allow
  }
}
